import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CucmQueriesResource } from '../../resources/cucm-queries.resource';
import { CustomCucmReport, Privilege } from '../../../../shared/models/generated/ziroModels';
import { combineLatest, Observable, Subscriber, Subscription } from 'rxjs';
import { ToastService } from '../../../../shared/services/toast.service';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { BottomNavButton } from '../../../../shared/bottom-nav/bottom-nav.component';
import { first, map } from 'rxjs/operators';
import { AuthenticationContext } from '../../../../shared/contexts/authentication.context';
import { SiteSummaryContext } from '../../../../shared/contexts/site-summary.context';
import { DateTimeFormat } from '../../../../shared/filters/date-time-format.filter';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';

interface CustomReportsRow extends DatatableRow {
  id: number;
  reportName: string;
  serverId: string;
  lastUpdated: string;
}

@Component({
  selector: 'ziro-custom-reports-list',
  templateUrl: './custom-reports-list.component.html',
  styleUrls: ['../../../reporting.scss'],
  providers: [DateTimeFormat],
  standalone: false,
})
export class CustomReportsListComponent implements OnInit, OnDestroy {
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _cucmQueriesResource = inject(CucmQueriesResource);
  private siteSummaryContext = inject(SiteSummaryContext);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _toastService = inject(ToastService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private authenticationContext = inject(AuthenticationContext);
  private _dateTimeFormat = inject(DateTimeFormat);

  cucmServers: { id: number; description: string }[] = [];
  serverIdLabelMap: { [key: number]: string } = {};
  isLoading = true;
  isReadOnly: boolean;
  exportingReports = new Set<number>();
  reports: CustomCucmReport[] = [];
  filteredReports: CustomCucmReport[] = [];
  columns: DatatableColumn<CustomReportsRow>[] = [
    {
      name: 'reportName',
      columnSize: 'col-sm-4',
      label: 'tkey;reports.custom_reports.custom_report_list.report_name',
    },
    {
      name: 'serverId',
      columnSize: 'col-sm-2',
      label: 'tkey;reports.custom_reports.custom_report_list.cucm_server',
    },
    {
      name: 'lastUpdated',
      columnSize: 'col-sm-3',
      label: 'tkey;reports.custom_reports.custom_report_list.last_updated',
    },
  ];
  tableRows: CustomReportsRow[] = [];
  serverOptions: string[] = [];

  private _subs = new Subscription();

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;reports.custom_reports.title' }]);

    const reportObservable = this._refreshTable();

    const authenticationObservable = this.authenticationContext.state$.pipe(
      map((user) => {
        this.isReadOnly = !user.privileges.includes(Privilege.CONTROL_WRITE);
      }),
    );

    this._subs.add(
      combineLatest([reportObservable, authenticationObservable]).subscribe(() => {
        this._initBottomNav();
        this.isLoading = false;
      }),
    );
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  isServerMatch(filters: string[], row: CustomReportsRow): boolean {
    return !filters.length || filters.includes(row.serverId);
  }

  private _refreshTable(): Observable<void> {
    return combineLatest([this.siteSummaryContext.state$, this._cucmQueriesResource.getAllReports()]).pipe(
      first(),
      map(([siteSummary, reports]) => {
        this.serverIdLabelMap = siteSummary.clusters.reduce<{ [id: number]: string }>((serverMap, cluster) => {
          if (!serverMap[cluster.cucmServerId]) {
            serverMap[cluster.cucmServerId] = cluster.cucmServerDescription;
          }
          return serverMap;
        }, {});
        this.cucmServers = Object.entries(this.serverIdLabelMap).map(([key, value]) => {
          return { id: Number(key), description: value };
        });
        this.serverOptions = this.cucmServers.map((server) => server.description);

        this.reports = reports;
        this.filteredReports = reports;
        this._createTableRows();
      }),
    );
  }

  deleteClicked(report: CustomCucmReport): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center text-danger',
        title: this._translateService.instant('tkey;reports.custom_reports.custom_report_list.delete.modal.title'),
        promptBody: this._translateService.instant(
          'tkey;reports.custom_reports.custom_report_list.delete.modal.message',
          {
            reportName: report.reportName,
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._deleteReport(report),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  exportClicked(report: CustomCucmReport): void {
    this.exportingReports.add(report.id);
    this._createTableRows();
    this._cucmQueriesResource.getExportToExcel(report.id).subscribe(() => {
      this._toastService.push(
        ToastTypes.SUCCESS,
        ZiroIcons.EXPORT,
        'tkey;reports.xlsx_export.downloaded_toast.title',
        'tkey;reports.xlsx_export.downloaded_toast.message',
      );
      this.exportingReports.delete(report.id);
      this._createTableRows();
    });
  }

  editClicked(report: CustomCucmReport): void {
    const id = report.id;
    this._router.navigate([id], { relativeTo: this._route });
  }

  private _deleteReport(report: CustomCucmReport): Observable<void> {
    this.isLoading = true;
    return new Observable((subscriber: Subscriber<void>) => {
      this._cucmQueriesResource.deleteReport(report.id).subscribe(() => {
        this._toastService.pushDeleteToast(
          'tkey;reports.custom_reports.custom_report_list.toast.title',
          report.reportName,
        );
        this._refreshTable().subscribe(() => (this.isLoading = false));
        subscriber.next(null);
        subscriber.complete();
      });
    });
  }

  private _initBottomNav(): void {
    const bottomNavButtons: BottomNavButton[] = [];

    if (!this.isReadOnly) {
      bottomNavButtons.push({
        id: 'customReportsAddReport',
        dataAutomation: 'custom-reports-add-report-button',
        label: 'tkey;reports.custom_reports.add_report.label',
        icon: ZiroIcons.ADD,
        buttonClass: ButtonStyles.PRIMARY,
        type: ButtonTypes.LINK,
        href: window.location.href + `/new-report`,
      });
    }

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _createTableRows(): void {
    this.tableRows = this.filteredReports.map((report) => {
      return {
        id: report.id,
        reportName: report.reportName,
        serverId: this.serverIdLabelMap[report.serverId],
        lastUpdated: this._dateTimeFormat.transform(report.lastUpdated),
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'report-delete',
            icon: ZiroIcons.DELETE_SMALL,
            isHidden: this.isReadOnly,
            disabled: this.exportingReports.has(report.id),
            disabledTooltip: 'tkey;reports.xlsx_edit_delete.button.disabled.tooltip',
            onClick: () => this.deleteClicked(report),
          },
          {
            style: ButtonStyles.OUTLINE_PRIMARY,
            type: ButtonTypes.LINK,
            href: window.location.href + `/${report.id}`,
            dataAutomation: 'report-edit',
            icon: ZiroIcons.EDIT,
            isHidden: this.isReadOnly,
            disabled: this.exportingReports.has(report.id),
            disabledTooltip: 'tkey;reports.xlsx_edit_delete.button.disabled.tooltip',
            onClick: () => this.editClicked(report),
          },
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'report-export',
            icon: ZiroIcons.EXPORT,
            disabled: this.exportingReports.has(report.id),
            label: 'tkey;reports.xlsx_export.button',
            isPending: this.exportingReports.has(report.id),
            iconPending: ZiroIcons.SAVING,
            onClick: () => this.exportClicked(report),
          },
        ],
        actionsColumnSize: 'col-sm-3',
      };
    });
  }
}
